import * as React from 'react';
import { useForkRef } from '../../hooks';
import { FixMeLater, inDOM, ReactElementWithRef } from '../../utils';

const pollInterval = 100;

export type ObserverProps = {
    /**
     * The content of the component.
     */
    children: ReactElementWithRef<Element>;

    /**
     * Callback fired when the component becomes 'in view'.
     */
    onViewStart?: () => void;

    /**
     * Callback fired when the component ceases to be 'in view'.
     */
    onViewEnd?: () => void;

    /**
     * Callback fired when the component is considered as having been viewed.
     */
    onViewComplete: () => void;

    /**
     * Callback fired when the child component is rendered.
     */
    onImpression?: () => void;

    /**
     * The proportion of the component that must be visible for the component to be considered as 'in view'.
     * @default 0.5
     */
    viewThreshold?: number;

    /**
     * The time in milliseconds that the component must be 'in view' before it can be considered as having been viewed.
     * @default 1000
     */
    viewTimeout?: number;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
};

export function Observer(props: ObserverProps): React.JSX.Element | null {
    const {
        children,
        viewThreshold = 0.5,
        onViewStart,
        onViewEnd,
        onViewComplete,
        onImpression,
        viewTimeout = 1000,
        ref,
        ...other
    } = props;

    const isClient = inDOM();

    const observerRef = React.useRef<IntersectionObserver | null>(null);

    const [inView, setInView] = React.useState<boolean>(false);

    // The total number of milliseconds that the component has been in view.
    const millisecondsInView = React.useRef(0);

    // The last time the component came into view or the last time 'millisecondsInView' was updated.
    // The value will be null when the component is out of view.
    const lastViewedTimestamp = React.useRef<number | null>(null);

    // An interval to track how long the component has been in view for.
    const pollingInterval = React.useRef<number | null>(null);

    const callback = React.useCallback((entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
            setInView(entry.isIntersecting);
        });
    }, []);

    const handleElementRef = React.useCallback(
        (element: HTMLElement | null) => {
            if (isClient && element) {
                if (!observerRef.current) {
                    observerRef.current = new IntersectionObserver(callback, {
                        threshold: viewThreshold,
                    });
                }
                observerRef.current.observe(element);
            }
        },
        [callback, isClient, viewThreshold]
    );

    const checkTimeInView = React.useCallback(() => {
        if (lastViewedTimestamp.current && millisecondsInView.current < viewTimeout) {
            // The component is in view but we have not yet reached the timeout.
            const now = Date.now();
            if (now > lastViewedTimestamp.current) {
                // Capture how many milliseconds have elapsed since we last checked that the component was in view.
                const elapsedMilliseconds = now - lastViewedTimestamp.current;
                millisecondsInView.current += elapsedMilliseconds;
                lastViewedTimestamp.current = now;
            }

            if (millisecondsInView.current >= viewTimeout) {
                // The component has now been in view for the required amount of time.
                // Fire the view event and stop polling.
                onViewComplete();
                if (pollingInterval.current) {
                    clearInterval(pollingInterval.current);
                }
            }
        }
    }, [onViewComplete, viewTimeout]);

    // Track when the component comes in and out of view
    React.useEffect(() => {
        if (inView) {
            // Start polling if we haven't already
            if (pollingInterval.current == null) {
                pollingInterval.current = window.setInterval(checkTimeInView, pollInterval);
            }
            if (onViewStart) {
                onViewStart();
            }
            lastViewedTimestamp.current = Date.now();
        } else if (onViewEnd) {
            onViewEnd();
            lastViewedTimestamp.current = null;
        }
    }, [checkTimeInView, inView, onViewEnd, onViewStart]);

    React.useEffect(() => {
        // The impression event should be fired once when the component mounts
        if (onImpression) {
            onImpression();
        }
        return () => {
            // Disconnect the observer when the component unmounts
            observerRef.current?.disconnect();
            // Stop polling if we haven't already.
            if (pollingInterval.current) {
                clearInterval(pollingInterval.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRef = useForkRef(handleElementRef, ref);

    const child = React.Children.only(children);
    if (!React.isValidElement(child)) {
        return null;
    }

    return React.cloneElement(child, {
        ref: handleRef,
        ...other,
    });
}
